import { PHONE_ERROR_NAME_PATTERN, phoneErrorMessageOf, type PhoneErrorSource, type TelephonyProviderName } from "@gioia365/contracts";
import type { AudioChoice } from "./audio-devices";

// Il telefono del browser (piano M4, D10): la porta con cui l'interfaccia compone una chiamata in uscita e ne sente gli
// eventi, senza conoscere il fornitore. Le implementazioni sono due: quella di Twilio (`twilio-phone.ts`, con il SDK
// scaricato in sottofondo appena il guscio di chi chiama è pronto, `preload`), che `main.tsx` dà all'applicazione, e quella
// finta dei test (`fake-phone.ts`).
// Il codice viene dalla pagina «Prova chiamate» di R1 (decisione A13 del controllore), tolta il 5 ottobre 2026 dopo la prima
// chiamata vera dalla scheda di un Lead.

/**
 * Ciò che chi chiama sente di una chiamata. `onError` può arrivare anche dopo `onClosed`: il SDK chiude prima di dirlo. Il
 * `detail`, quando c'è, serve soltanto al resoconto per il server (TEL-33), mai all'interfaccia.
 */
export type BrowserCallEvents = {
  onRinging(): void;
  onAccepted(): void;
  onClosed(): void;
  onError(error: BrowserPhoneError, detail?: PhoneErrorDetail): void;
};

/**
 * Il dettaglio tecnico di un errore, per il resoconto al server (TEL-33): da dove arriva, il codice del fornitore, il nome
 * dell'errore (`twilioError.name`) e il messaggio già pulito (`phoneErrorMessageOf`: cifre lunghe e gettoni mascherati).
 */
export type PhoneErrorDetail = { source: PhoneErrorSource; code: number | null; name: string | null; message: string | null };

/** Un errore del telefono del browser: il microfono negato, il SDK che non si carica, o un errore del fornitore col suo codice. */
export type BrowserPhoneError = { kind: "microphone" } | { kind: "load" } | { kind: "twilio"; code: number | null };

/**
 * Una chiamata composta. `switchAudio` porta sulla chiamata in corso il microfono e l'ascolto scelti nella postazione
 * (richiesta del product owner del 6 ottobre 2026); un dispositivo che manca lascia quello di prima, senza errori.
 */
export type BrowserCall = { hangUp(): void; switchAudio(choice: AudioChoice): void };

/**
 * La porta. `call` compone con il token del browser e i parametri che il webhook della voce del fornitore legge (per le
 * chiamate dalla scheda soltanto `callId`: numero chiamato e numero mostrato li decide il server, D4). Risolve appena la
 * chiamata è creata, prima che squilli: da lì in poi parlano gli eventi. Rifiuta se il telefono non si carica o non crea la
 * chiamata; il motivo si legge con `browserPhoneErrorOf`. `audio` è il microfono (e l'ascolto) scelto su questo computer, da
 * usare prima di comporre: se manca o non risponde, la chiamata parte comunque con quello di sistema.
 */
export type BrowserPhone = {
  call(input: {
    provider: TelephonyProviderName;
    token: string;
    params: Readonly<Record<string, string>>;
    events: BrowserCallEvents;
    audio: AudioChoice;
  }): Promise<BrowserCall>;
  /**
   * Scarica il telefono senza chiamare, in sottofondo (prova del Power Dialer del 6 ottobre 2026): una pagina aperta prima di un
   * aggiornamento ha già il SDK in memoria, e la sua prima chiamata parte anche se i file del SDK sul server sono cambiati. Mai
   * un errore e mai un'attesa: se il download fallisce (rete) lo riprova `call`.
   */
  preload(): void;
};

/** Twilio: il browser o la persona hanno negato il microfono (`UserMediaErrors.PermissionDeniedError`). */
const MICROPHONE_DENIED = 31401;

/**
 * Errore lanciato quando il SDK non si carica (o il fornitore non ha un telefono del browser): l'interfaccia lo dice con il suo
 * testo. `reason` è l'errore del caricamento, per il resoconto al server.
 */
export class BrowserPhoneLoadError extends Error {
  readonly reason: unknown;
  constructor(reason?: unknown) {
    super("Voice SDK not loaded");
    this.name = "BrowserPhoneLoadError";
    this.reason = reason;
  }
}

export function browserPhoneErrorOf(error: unknown): BrowserPhoneError {
  if (error instanceof BrowserPhoneLoadError) return { kind: "load" };
  // Gli errori del SDK hanno `code` (il codice di Twilio) e `name`; quelli del browser soltanto `name`.
  const { code, name } = typeof error === "object" && error !== null ? (error as { code?: unknown; name?: unknown }) : {};
  if (code === MICROPHONE_DENIED || name === "NotAllowedError" || name === "PermissionDeniedError") return { kind: "microphone" };
  return { kind: "twilio", code: typeof code === "number" ? code : null };
}

/**
 * Il dettaglio tecnico di un errore qualunque, per il resoconto (TEL-33): codice e nome soltanto se hanno la forma attesa,
 * il messaggio pulito. Del SDK che non si carica conta l'errore del caricamento. Non lancia mai.
 */
export function phoneErrorDetailOf(error: unknown, source: PhoneErrorSource): PhoneErrorDetail {
  try {
    const raw = error instanceof BrowserPhoneLoadError && error.reason !== undefined ? error.reason : error;
    const { code, name, message } = typeof raw === "object" && raw !== null ? (raw as { code?: unknown; name?: unknown; message?: unknown }) : {};
    return {
      source,
      code: typeof code === "number" && Number.isInteger(code) && code >= 0 && code <= 999_999 ? code : null,
      name: typeof name === "string" && PHONE_ERROR_NAME_PATTERN.test(name) ? name : null,
      message: phoneErrorMessageOf(typeof raw === "string" ? raw : message),
    };
  } catch {
    // Un errore strano (un getter che lancia) non ferma mai la chiamata: il resoconto parte senza dettagli.
    return { source, code: null, name: null, message: null };
  }
}
